import {
  Check as CheckIcon,
  Close as CloseIcon,
  ThumbDown as ThumbDownIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useId, useRef } from 'react';
import {
  MAX_FEEDBACK_COMMENT_LENGTH,
  type FeedbackRating,
  type FeedbackReasonCode,
} from '../../../api-clients/sessions';
import {
  NOTE_FEEDBACK_CONFIRMATION,
  NOTE_FEEDBACK_LIMIT_MESSAGE,
  NOTE_FEEDBACK_SAVE_FAILED,
  useNoteFeedback,
} from '../hooks/useNoteFeedback';
import { noteFeedbackStyles as styles } from '../styles/noteFeedbackStyles';

const PANELS: Record<
  FeedbackRating,
  { title: string; reasons: { code: FeedbackReasonCode; label: string }[] }
> = {
  up: {
    title: 'What did you like?',
    reasons: [
      { code: 'accurate', label: 'Accurate' },
      { code: 'complete', label: 'Complete' },
      { code: 'well_organized', label: 'Well organized' },
      { code: 'saved_me_time', label: 'Saved me time' },
    ],
  },
  down: {
    title: 'What went wrong?',
    reasons: [
      { code: 'inaccurate', label: 'Inaccurate' },
      { code: 'missed_details', label: 'Missed details' },
      { code: 'formatting', label: 'Formatting' },
      { code: 'required_edits', label: 'Required edits' },
    ],
  },
};

const THUMB_LABELS: Record<FeedbackRating, string> = {
  up: 'Rate this note helpful',
  down: 'Rate this note not helpful',
};

// The counter appears once the comment is within 10% of the API's limit.
const COUNTER_THRESHOLD = MAX_FEEDBACK_COMMENT_LENGTH * 0.9;

type FocusTarget = 'panel' | 'confirmed' | FeedbackRating;

interface NoteFeedbackProps {
  sessionId: string;
  documentId: string;
}

export const NoteFeedback = ({ sessionId, documentId }: NoteFeedbackProps) => {
  const {
    view,
    rating,
    saving,
    failure,
    limitReached,
    draft,
    announcement,
    rate,
    submit,
    retry,
    dismiss,
    reopen,
    toggleReason,
    setComment,
  } = useNoteFeedback({ sessionId, documentId });

  const titleId = useId();

  // Focus moves only after a user action, never when the footer first renders.
  const pendingFocus = useRef<FocusTarget | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmedRef = useRef<HTMLButtonElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);
  const thumbRefs = {
    up: useRef<HTMLButtonElement>(null),
    down: useRef<HTMLButtonElement>(null),
  };

  useEffect(() => {
    const target = pendingFocus.current;
    if (!target || saving) return;
    // A shown failure or limit message takes focus over the action's target.
    const element =
      failureRef.current ??
      (target === 'panel'
        ? panelRef.current
        : target === 'confirmed'
          ? confirmedRef.current
          : thumbRefs[target].current);
    if (!element) return;
    pendingFocus.current = null;
    element.focus();
  });

  const handleRate = (next: FeedbackRating) => {
    pendingFocus.current = 'panel';
    rate(next);
  };

  const handleSubmit = () => {
    pendingFocus.current = 'confirmed';
    submit();
  };

  const handleRetry = () => {
    pendingFocus.current =
      failure?.action.kind === 'submit' ? 'confirmed' : 'panel';
    retry();
  };

  const handleDismiss = (method: 'skip' | 'close') => {
    pendingFocus.current = 'confirmed';
    dismiss(method);
  };

  const handleReopen = () => {
    pendingFocus.current = rating;
    reopen();
  };

  const filledThumbSx = (value: FeedbackRating) =>
    value === 'up' ? styles.filledThumbUp : styles.filledThumbDown;

  const renderFilledIcon = (value: FeedbackRating) =>
    value === 'up' ? (
      <ThumbUpIcon fontSize='small' />
    ) : (
      <ThumbDownIcon fontSize='small' />
    );

  const renderFailureText = () => {
    if (limitReached) return NOTE_FEEDBACK_LIMIT_MESSAGE;
    if (!failure?.retryable) return NOTE_FEEDBACK_SAVE_FAILED;
    return (
      <>
        Couldn&apos;t save —{' '}
        <Button
          variant='text'
          onClick={handleRetry}
          disabled={saving}
          sx={styles.retryButton}
        >
          Retry
        </Button>
      </>
    );
  };

  const renderFailure = () =>
    (limitReached || failure) && (
      <Box ref={failureRef} tabIndex={-1} sx={styles.failureRow}>
        <Typography variant='body2' sx={styles.failureText}>
          {renderFailureText()}
        </Typography>
      </Box>
    );

  const renderThumb = (value: FeedbackRating) => {
    const pressed = rating === value;
    return (
      <IconButton
        ref={thumbRefs[value]}
        size='small'
        aria-label={THUMB_LABELS[value]}
        aria-pressed={pressed}
        disabled={saving || limitReached}
        onClick={() => handleRate(value)}
        sx={pressed ? filledThumbSx(value) : styles.outlineThumb}
      >
        {pressed ? (
          renderFilledIcon(value)
        ) : value === 'up' ? (
          <ThumbUpOutlinedIcon fontSize='small' />
        ) : (
          <ThumbDownOutlinedIcon fontSize='small' />
        )}
      </IconButton>
    );
  };

  const renderCollapsed = () => (
    <>
      <Box sx={styles.row}>
        <Typography variant='body2' sx={styles.prompt}>
          How was this note?
        </Typography>
        {renderThumb('up')}
        {renderThumb('down')}
      </Box>
      {renderFailure()}
    </>
  );

  const renderConfirmed = (value: FeedbackRating) => (
    <Box sx={styles.row}>
      <IconButton
        ref={confirmedRef}
        size='small'
        aria-label={`Change your rating (rated ${value === 'up' ? 'helpful' : 'not helpful'})`}
        onClick={handleReopen}
        sx={filledThumbSx(value)}
      >
        {renderFilledIcon(value)}
      </IconButton>
      <Typography variant='body2' sx={styles.confirmation}>
        {NOTE_FEEDBACK_CONFIRMATION}
      </Typography>
    </Box>
  );

  const renderPanel = (value: FeedbackRating) => {
    const { title, reasons } = PANELS[value];
    const canSubmit =
      draft.reasonCodes.length > 0 || draft.comment.trim() !== '';
    // Retry and Submit resend the failed submit, so its content must stay as shown.
    const locked = saving || failure?.action.kind === 'submit';
    return (
      <Box
        ref={panelRef}
        tabIndex={-1}
        role='group'
        aria-labelledby={titleId}
        sx={styles.panel}
      >
        <Box sx={styles.panelHeader}>
          <Box
            aria-hidden
            sx={value === 'up' ? styles.thumbBadgeUp : styles.thumbBadgeDown}
          >
            {renderFilledIcon(value)}
          </Box>
          <Typography id={titleId} variant='subtitle2' sx={styles.panelTitle}>
            {title}
          </Typography>
          <IconButton
            size='small'
            aria-label='Close feedback'
            disabled={saving}
            onClick={() => handleDismiss('close')}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <Box role='group' aria-label='Reasons' sx={styles.chips}>
          {reasons.map(({ code, label }) => {
            const selected = draft.reasonCodes.includes(code);
            return (
              <Chip
                key={code}
                component='button'
                clickable
                label={label}
                aria-pressed={selected}
                variant={selected ? 'filled' : 'outlined'}
                color={selected ? 'primary' : 'default'}
                icon={selected ? <CheckIcon /> : undefined}
                disabled={locked}
                onClick={() => toggleReason(code)}
              />
            );
          })}
        </Box>

        {/* The aria-label also names Datadog RUM click actions, so the typed
            comment is never used as an action name. */}
        <TextField
          multiline
          fullWidth
          minRows={2}
          size='small'
          placeholder='Tell us more (optional)'
          value={draft.comment}
          disabled={locked}
          onChange={(event) => setComment(event.target.value)}
          slotProps={{
            htmlInput: {
              maxLength: MAX_FEEDBACK_COMMENT_LENGTH,
              'aria-label': 'Tell us more (optional)',
            },
          }}
        />
        {draft.comment.length >= COUNTER_THRESHOLD && (
          <Typography variant='caption' sx={styles.characterCount}>
            {draft.comment.length.toLocaleString()} /{' '}
            {MAX_FEEDBACK_COMMENT_LENGTH.toLocaleString()} characters
          </Typography>
        )}

        {renderFailure()}

        <Box sx={styles.panelFooter}>
          <Button
            variant='text'
            color='secondary'
            disabled={saving}
            onClick={() => handleDismiss('skip')}
          >
            Skip
          </Button>
          <Button
            variant='pill'
            onClick={handleSubmit}
            disabled={saving || !canSubmit || limitReached}
            sx={styles.submitButton}
          >
            Submit
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={styles.container} data-testid='note-feedback' aria-busy={saving}>
      {view === 'panel' && rating
        ? renderPanel(rating)
        : view === 'confirmed' && rating
          ? renderConfirmed(rating)
          : renderCollapsed()}
      <Box role='status' aria-live='polite' sx={styles.visuallyHidden}>
        {announcement}
      </Box>
    </Box>
  );
};
